import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query'
import { useCallback } from 'react'
import { api } from '../../api/client'
import type { components } from '../../api/generated/schema'
import { keys } from '../../api/keys'
import { parseMoney, signedTransactionCents } from '../../api/parseMoney'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { RulesError } from './useRules'

/**
 * What applying the rule would do to one match, in the words the row shows.
 *
 * - `will_change`: "Will change" (`filled`, `overwritten`).
 * - `already_right`: "Already right" (`already_correct`).
 * - `you_set`: "You set this" (`skipped_overwrites`): the field holds another value, so a fill-only
 *   apply leaves it. iOS #995: these are NOT "already right".
 * - `removed`: "Removed" (`skipped_excluded`): taken off this rule for good.
 * - `outranked`: "Another rule" (`skipped_outranked`): a higher rule holds the field.
 * - `blocked`: "Can't change" (`skipped_inaccessible`, `skipped_locked`, and any bucket this client
 *   does not know): nothing an apply here would write.
 *
 * Only `will_change` and `you_set` can be ticked.
 */
export type MatchStatus = 'will_change' | 'already_right' | 'you_set' | 'removed' | 'outranked' | 'blocked'

export const MATCH_STATUS_LABEL: Record<MatchStatus, string> = {
  will_change: 'Will change',
  already_right: 'Already right',
  you_set: 'You set this',
  removed: 'Removed',
  outranked: 'Another rule',
  blocked: "Can't change",
}

export const canTick = (s: MatchStatus) => s === 'will_change' || s === 'you_set'

/** Never defaults an unknown bucket to something tickable: it reads "Can't change". */
export function statusOf(bucket: string): MatchStatus {
  switch (bucket) {
    case 'filled':
    case 'overwritten':
      return 'will_change'
    case 'already_correct':
      return 'already_right'
    case 'skipped_overwrites':
      return 'you_set'
    case 'skipped_excluded':
      return 'removed'
    case 'skipped_outranked':
      return 'outranked'
    default:
      return 'blocked'
  }
}

/** One transaction the rule matches today. `amountCents` is integer cents in DISPLAY sign; the
 *  match carries no currency, so the row reads it from the transaction. */
export type RuleMatch = {
  transactionId: string
  merchantText: string | null
  /** YYYY-MM-DD. */
  date: string
  amountCents: number
  status: MatchStatus
}
export type RuleMatchPage = { matches: RuleMatch[] }

/** Rows per request. The server allows up to 200; each row also reads its transaction by id. */
export const MATCH_PAGE_SIZE = 25

type Page = components['schemas']['RuleMatchPageResponse']

const toMatch = (m: components['schemas']['RuleMatchItem']): RuleMatch => ({
  transactionId: m.transaction_id,
  merchantText: m.merchant_name,
  date: m.transaction_date,
  // A decimal STRING, expense-positive in storage; parsed once here and re-signed for display.
  amountCents: signedTransactionCents(parseMoney(m.amount)),
  status: statusOf(m.bucket),
})

function flatten(data: InfiniteData<Page, string | null>): RuleMatchPage {
  const seen = new Set<string>()
  const matches: RuleMatch[] = []
  for (const p of data.pages) {
    for (const m of p.matches) {
      if (seen.has(m.transaction_id)) continue
      seen.add(m.transaction_id)
      matches.push(toMatch(m))
    }
  }
  return { matches }
}

/**
 * `GET /rules/{id}/matches`, keyset-paged over the whole history: every transaction the rule's
 * conditions match today, each with what an apply would do. `overwrite_existing=false`, so a row
 * holding another value reads "You set this" rather than "Will change".
 *
 * A short page is not the end (the server stops on its own scan budget): `hasMore` follows
 * `next_cursor`, never the page's length. There is no total, so the count is known only once the
 * last page is in (`complete`).
 *
 * Under the `rules` root, so an apply, an exclusion and an Undo all refresh it.
 */
export function useRuleMatches(ruleId: string): {
  state: AsyncState<RuleMatchPage>
  hasMore: boolean
  loadingMore: boolean
  loadMore: () => void
} {
  const result = useInfiniteQuery({
    queryKey: keys.rules.matches(ruleId),
    queryFn: async ({ pageParam, signal }): Promise<Page> => {
      const { data, error, response } = await api.GET('/api/v1/rules/{rule_id}/matches', {
        params: {
          path: { rule_id: ruleId },
          query: { overwrite_existing: false, page_size: MATCH_PAGE_SIZE, ...(pageParam === null ? {} : { cursor: pageParam }) },
        },
        signal,
      })
      if (error !== undefined || data === undefined) throw new RulesError(error, response.status)
      return data
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    select: flatten,
  })
  const state = fromQuery(result, {
    message: () => "This rule's matches could not be loaded.",
    recovery: { label: 'Try again', onAct: () => void result.refetch() },
    // Empty only once the scan has finished: a first page that came back short on the server's
    // budget, with a cursor to go on, is not "matches nothing".
    isEmpty: (d) => d.matches.length === 0 && !result.hasNextPage,
  })
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = result
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  return { state, hasMore: hasNextPage, loadingMore: isFetchingNextPage, loadMore }
}
